/**
 * @file Wave director tuning (GDD §Fase 3 — oleadas).
 *
 * Difficulty is expressed as a budget, not a hand-authored list. Each wave gets
 * a point budget that grows with the wave number, and the director spends it on
 * whatever archetypes have unlocked. New enemies join the rotation by adding a
 * cost and an unlock wave — the director itself never changes.
 */

export const WAVES = Object.freeze({
  /** Seconds of calm before wave 1. */
  firstWaveDelay: 2.6,
  /** Seconds of calm between a cleared wave and the next one. */
  breather: 4,
  /** Seconds between individual spawns inside a wave. */
  spawnInterval: 0.42,
  /** Warning ring shown at a spawn point before the enemy materialises. */
  telegraphTime: 0.85,

  /** Budget for wave 1, and its geometric growth per wave. */
  budgetBase: 6,
  budgetGrowth: 1.28,
  /** Budget stops growing here so late waves stay winnable. */
  budgetCap: 90,

  /** Hard ceiling on simultaneous enemies, for frame budget. */
  maxAlive: 90,
  /** Spawns are never placed closer than this to the player. */
  minPlayerDistance: 17,

  /** Budget cost per archetype. */
  costs: {
    grunt: 1,
    runner: 1.4,
    spitter: 2.4,
    burner: 2.8,
    exploder: 2,
    suicide_bomber: 2.6,
    brute: 7,
    enemy_ranged_01: 3.2,
    // Armed variants (issue #57): cost climbs with the threat the gun poses.
    gunner: 3,
    shotgunner: 3.5,
    sniper: 4,
    rocketeer: 6.5
  },

  /** First wave on which each archetype may appear. */
  unlocks: {
    grunt: 1,
    runner: 1,
    spitter: 2,
    burner: 3,
    exploder: 5,
    suicide_bomber: 4,
    brute: 7,
    enemy_ranged_01: 4,
    // Staggered so the player meets each new gun one at a time, not all at once.
    gunner: 4,
    shotgunner: 5,
    sniper: 6,
    rocketeer: 8
  },

  /**
   * Relative pick weight once unlocked. Grunts stay common so the arena never
   * becomes all specials.
   */
  weights: {
    grunt: 10,
    runner: 7,
    spitter: 5,
    burner: 4,
    exploder: 4,
    suicide_bomber: 4,
    brute: 3,
    enemy_ranged_01: 4,
    // Rarer than the melee rank-and-file; the rocketeer is a genuine elite.
    gunner: 4,
    shotgunner: 3,
    sniper: 3,
    rocketeer: 2
  }
});

/**
 * Point budget available to one wave.
 *
 * @param {number} wave Wave number, 1-based.
 * @param {typeof WAVES} [tuning] Table to read; defaults to the shipped balance.
 * @returns {number} Points the director may spend.
 */
export function computeWaveBudget(wave, tuning = WAVES) {
  return Math.min(tuning.budgetCap, tuning.budgetBase * tuning.budgetGrowth ** (wave - 1));
}

/**
 * @param {number} wave Wave number, 1-based.
 * @param {number} [levelIndex] Dungeon floor, 1-based.
 * @returns {number} Unlock tier reached by this wave on this floor.
 */
export function progressionWave(wave, levelIndex = 1) {
  const safeWave = Math.max(1, Math.floor(wave));
  const safeLevel = Math.max(1, Math.floor(levelIndex));
  return safeWave + safeLevel - 1;
}

/**
 * @param {number} wave Wave number, 1-based.
 * @param {typeof WAVES} [tuning] Table to read; defaults to the shipped balance.
 * @param {number} [levelIndex] Dungeon floor, 1-based.
 * @returns {string[]} Archetype keys that may appear on this wave.
 */
export function unlockedArchetypes(wave, tuning = WAVES, levelIndex = 1) {
  const tier = progressionWave(wave, levelIndex);
  return Object.keys(tuning.costs).filter((key) => tier >= (tuning.unlocks[key] ?? 1));
}

/**
 * Chooses the archetypes for one wave by spending its budget.
 *
 * The table is a parameter rather than the imported constant so the live tuning
 * layer ({@link ./WaveTuning.js}) can feed edited numbers in without this file
 * knowing that a debug window exists.
 *
 * @param {number} wave Wave number, 1-based.
 * @param {typeof WAVES} [tuning] Table to read; defaults to the shipped balance.
 * @param {number} [levelIndex] Dungeon floor, 1-based.
 * @returns {string[]} Archetype keys, in spawn order.
 */
export function rollWaveComposition(wave, tuning = WAVES, levelIndex = 1) {
  const budget = computeWaveBudget(wave, tuning);

  const available = unlockedArchetypes(wave, tuning, levelIndex);
  /** @type {string[]} */
  const composition = [];
  let remaining = budget;

  // Spend greedily but randomly; stop as soon as nothing is affordable.
  while (remaining > 0) {
    const affordable = available.filter((key) => tuning.costs[key] <= remaining);
    if (affordable.length === 0) break;

    const key = pickWeighted(affordable, tuning.weights);
    composition.push(key);
    remaining -= tuning.costs[key];

    if (composition.length >= tuning.maxAlive) break;
  }

  // Cheap fodder first, heavies last: the wave escalates as it lands.
  composition.sort((a, b) => tuning.costs[a] - tuning.costs[b]);
  return composition;
}

/**
 * @param {string[]} keys Candidate archetype keys.
 * @param {Record<string, number>} weights Pick weights, by archetype.
 * @returns {string} One key, chosen by weight.
 */
function pickWeighted(keys, weights) {
  let total = 0;
  for (const key of keys) total += weights[key] ?? 1;

  let roll = Math.random() * total;
  for (const key of keys) {
    roll -= weights[key] ?? 1;
    if (roll <= 0) return key;
  }
  return keys[keys.length - 1];
}
